import { zillowApi } from "api/zillow";
import { apartmentsComApi } from "api/apartmentsCom";
import { useMutation } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Radio } from "shared/components";
import { Card } from "shared/components/Card";
import GoogleAnalyticsIcon from "shared/icons/brands/GoogleAnalytics-brand-icon.svg?react";
import HubSpotIcon from "shared/icons/brands/hubspot.svg?react";
import { cx } from "shared/utils";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  IntegrationId,
  Integration,
  Step
} from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { useSettingsController } from "views/settings-integration/controller";

interface FirstStepProps {
  selectedIntegration: string;
  setSelectedIntegration: (id: string) => void;
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
}

const BASE_INTEGRATIONS: Integration[] = [
  {
    id: IntegrationId.GA,
    name: "Google Analytics",
    icon: <GoogleAnalyticsIcon />
  },
  {
    id: IntegrationId.HUBSPOT,
    name: "HubSpot",
    icon: <HubSpotIcon />
  }
];

export const FirstStep = ({
  selectedIntegration,
  setSelectedIntegration,
  setCurrentStep,
  closeModal
}: FirstStepProps) => {
  const { currentSettings } = useSettingsController();
  const { type, partner, refreshPartnerData } = useAuthContext();

  const { mutate: connectZillow, isPending: isConnectingZillow } = useMutation({
    mutationKey: ["ZILLOW", "CREATE"],
    mutationFn: () => zillowApi.connect(),
    onSuccess: () => {
      refreshPartnerData();
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: connectApartmentsCom, isPending: isConnectingApartmentsCom } =
    useMutation({
      mutationKey: ["APARTMENTS_COM", "CREATE"],
      mutationFn: () => apartmentsComApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const integrations: Integration[] = [
    ...BASE_INTEGRATIONS,
    ...(type.isBoomCRM && !partner?.codebox_connected
      ? [
          {
            id: IntegrationId.CODEBOX,
            name: "Codebox",
            icon: <IntegrationLogoIcon id={IntegrationId.CODEBOX} size="BASE" />
          }
        ]
      : []),
    ...(type.isBoomCRM && !partner?.zillow_connected
      ? [
          {
            id: IntegrationId.ZILLOW,
            name: "Zillow",
            icon: <IntegrationLogoIcon id={IntegrationId.ZILLOW} size="BASE" />
          }
        ]
      : []),
    ...(type.isBoomCRM && !partner?.apartments_com_connected
      ? [
          {
            id: IntegrationId.APARTMENTS_COM,
            name: "Apartments.com",
            icon: (
              <IntegrationLogoIcon
                id={IntegrationId.APARTMENTS_COM}
                size="BASE"
              />
            )
          }
        ]
      : [])
  ];

  const handleContinue = () => {
    if (selectedIntegration === IntegrationId.ZILLOW) connectZillow();
    else if (selectedIntegration === IntegrationId.APARTMENTS_COM)
      connectApartmentsCom();
    else setCurrentStep(Step.SECOND);
  };

  return (
    <>
      {integrations.map((integration) => {
        const disabled =
          integration.id === IntegrationId.GA
            ? Boolean(currentSettings.google.googleAnalyticsId)
            : false;

        return (
          <Card
            className={cx(
              "p-6 flex justify-between m-1",
              disabled && "opacity-50 bg-alpha-40"
            )}
            key={integration.id}
            onClick={() => {
              if (!disabled) setSelectedIntegration(integration.id);
            }}
          >
            <div className="flex items-center gap-4">
              {integration.icon}
              <p className="font-bold text-base">{integration.name}</p>
            </div>
            <Radio
              className="mr-4 self-center cursor-pointer"
              id={integration.id}
              name={integration.name}
              onChange={() => null}
              checked={selectedIntegration === integration.id}
            />
          </Card>
        );
      })}
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={handleContinue}
        disabled={!selectedIntegration}
        loading={isConnectingZillow || isConnectingApartmentsCom}
        className="ml-auto mt-4"
      >
        Continue
      </Button>
    </>
  );
};
